import { stringify } from '@inwink/utils/querystring';

const URL_CONTEXTS: Record<string, IURLContext> = {
    event: {
        basePath: 'e',
        URLEntityConfig: {
            person: { path: 'registrations' },
            session: { path: 'sessions' },
            sessionperson: { path: 'sessionpersons' },
            exhibitor: { path: 'exhibitors' },
            order: { path: 'orders' },
            surveyanswer: { specificPath: handleSurveyAnswer },
            exhibitortask: { path: 'exhibitors/tasks' },
            exhibitortaskanswer: { specificPath: handleExhibitorTaskAnswer },
            exhibitorinvitation: { path: 'exhibitors/invitations' },
            meeting: { specificPath: handleBusinessMeeting },
            discussionthreadmessage: { specificPath: handleDiscussionThreadMessage }
        }
    },
    community: {
        basePath: 'c',
        URLEntityConfig: {
            member: { path: 'members' },
            ordercommunity: { path: 'orders' },
            article: { path: 'articles' },
            documentcommunity: { path: 'documents' },
            vod: { path: 'vods' },
            podcast: { path: 'podcasts' },
            forum: { path: 'forums' },
            forumsubject: { specificPath: handleForumSubject },
            iwevent: { path: 'events' },
            iweventregisteredmember: { specificPath: handleIWEventRegisteredMember },
            membershiplevel: { path: 'memberships' },
            company: { path: 'companies' },
            surveycommunity: { path: 'surveys' },
            surveyanswercommunity: { specificPath: handleSurveyAnswerCommunity },
            categorysubject: { path: 'categorysubject' },
            group: { path: 'groups' },
            groupmember: { specificPath: handleGroupMember },
            productcommunity: { path: 'products' },
            discountcodecommunity: { path: 'discountcodes' },
            communitymailtemplate: { path: 'mailtemplates' }
        }
    },
    tenant: {
        basePath: 'tenant',
        URLEntityConfig: {
            contact: { specificPath: handleTenantContact },
            authtenantcontact: { specificPath: handleTenantContact }
        }
    }
};

function handleDiscussionThreadMessage(params: IURLParams): Partial<ILocation> {
    const discussionMessageUrlPart = params.messageData?.exhibitorId ? "/exhibitors" : "";
    const discussionId = params.messageData?.discussionThreadId;
    return {
        pathname: `/${params.customerid}/e/${params.contextId}${discussionMessageUrlPart}/messenger`,
        search: discussionId ? stringify({ discussion: discussionId }) : ""
    };
}

function handleBusinessMeeting(params: IURLParams): Partial<ILocation> {
    const businessMeetingUrlPart = params.messageData?.meetingKind === "BusinessMeeting" ? "/businessmeeting" : "";
    return {
        pathname: `/${params.customerid}/e/${params.contextId}/meetings${businessMeetingUrlPart}`
    };
}

function handleSurveyAnswer(params: IURLParams): Partial<ILocation> {
    const parentEntityId = params.parentid || params.messageData?.surveyId;
    return {
        pathname: `/${params.customerid}/e/${params.contextId}/surveys/${parentEntityId}/answers`
    };
}

function handleExhibitorTaskAnswer(params: IURLParams): Partial<ILocation> {
    const parentEntityId = params.parentid || params.messageData?.exhibitorId;
    return {
        pathname: `/${params.customerid}/e/${params.contextId}/exhibitors/${parentEntityId}/tasks`
    };
}

function handleSurveyAnswerCommunity(params: IURLParams): Partial<ILocation> {
    return {
        pathname: `/${params.customerid}/c/${params.contextId}/surveys/${params.parentid}/answers`
    };
}

function handleTenantContact(params: IURLParams): Partial<ILocation> {
    return {
        pathname: `/${params.customerid}/tenant/${params.contextId}/contacts`
    };
}

function handleForumSubject(params: IURLParams): Partial<ILocation> {
    const parentEntityId = params.parentid || params.messageData?.forumId;
    return {
        pathname: `/${params.customerid}/c/${params.contextId}/forums/${parentEntityId}/subjects`
    };
}

function handleGroupMember(params: IURLParams): Partial<ILocation> {
    const parentEntityId = params.parentid || params.messageData?.groupId;
    let pathname: string;

    if (parentEntityId) {
        pathname = `/${params.customerid}/c/${params.contextId}/groupmembers/${parentEntityId}`;
    } else {
        pathname = `/${params.customerid}/c/${params.contextId}/groupmembers`;
    }

    return { pathname };
}

function handleIWEventRegisteredMember(params: IURLParams): Partial<ILocation> {
    const parentEntityId = params.parentid || params.messageData?.iweventId;

    return { 
        pathname: `/${params.customerid}/c/${params.contextId}/events/${parentEntityId}/registered` 
    };
}

interface ILocation {
    pathname: string;
    search: string;
}

interface INotificationURLContext {
    customerid: string,
    authTenantid: string,
    eventid: string,
    communityid: string,
}

interface IURLParams {
    customerid: string;
    contextId: string;
    id?: string;
    parentid?: string;
    messageData?: any;
}

interface IURLEntityConfig {
    // path ou specificPath l'un des 2 doit être défini
    path?: string; // Optionnel si specificPath est défini
    specificPath?: (params: IURLParams) => Partial<ILocation>;
    
    useParentId?: boolean;
    useMessageData?: boolean;
}

interface IURLContext {
    basePath: string;
    URLEntityConfig: Record<string, IURLEntityConfig>;
}

class NotificationURLBuilder {
    private static buildBasePath(context: string, customerid: string, contextId: string): string {
        const contextConfig = URL_CONTEXTS[context];
        if (!contextConfig) {
            throw new Error(`Unknown context: ${context}`);
        }
        return `/${customerid}/${contextConfig.basePath}/${contextId}/`;
    }

    private static buildSearchParams(id?: string): string {
        return id ? stringify({ showdetail: id }) : "";
    }
    
    private static buildLocationForEntity(
        context: string, 
        entityName: string, 
        params: IURLParams
    ): ILocation | null {
        const contextConfig = URL_CONTEXTS[context];
        const entityConfig = contextConfig.URLEntityConfig[entityName];

        if (!entityConfig) {
            return {
                pathname: this.buildBasePath(context, params.customerid, params.contextId),
                search: ""
            };
        }

        if (entityConfig.specificPath) {
            const specificResult = entityConfig.specificPath(params);
            return {
                pathname: specificResult.pathname || this.buildBasePath(context, params.customerid, params.contextId),
                search: specificResult.search ?? this.buildSearchParams(params.id)
            };
        }

        const basePath = this.buildBasePath(context, params.customerid, params.contextId);
        const pathname = `${basePath}${entityConfig.path}`;

        return {
            pathname,
            search: this.buildSearchParams(params.id)
        };
    }

    private static determineContext(notificationURLContext: INotificationURLContext): {
        context: string | null;
        contextId: string | null;
    } {
        const { eventid, communityid, authTenantid } = notificationURLContext;
        
        if (eventid) {
            return { context: 'event', contextId: eventid };
        }
        if (communityid) {
            return { context: 'community', contextId: communityid };
        }
        if (authTenantid) {
            return { context: 'tenant', contextId: authTenantid };
        }
        return { context: null, contextId: null };
    }

    public static createLocation(
        notificationURLContext: INotificationURLContext,
        entityName: string,
        id?: string,
        parentid?: string,
        messageData?: any
    ): ILocation | null {
        const normalizedEntityName = entityName.toLowerCase();

        const { context, contextId } = this.determineContext(notificationURLContext);

        if (!context || !contextId) {
            return null;
        }

        const params: IURLParams = {
            customerid: notificationURLContext.customerid,
            contextId,
            id,
            parentid,
            messageData
        };

        return this.buildLocationForEntity(context, normalizedEntityName, params);
    }
}

export function buildNotificationLocation(
    notificationURLContext: INotificationURLContext,
    entityname: string,
    id?: string,
    parentid?: string,
    messageData?: any
): ILocation | null {
    return NotificationURLBuilder.createLocation(
        notificationURLContext,
        entityname,
        id,
        parentid,
        messageData
    );
}

export type { ILocation, INotificationURLContext };
